import { useCallback, useRef, useState } from "react";
import type { ScrollRequest } from "../MessageListView";

interface UseScrollRequestArgs {
  onTargetMessageHandled?: () => void;
}

/**
 * Owns the channel-level "scroll to message X" request signal that
 * `MessageArea` hands to `MessageListView`.
 *
 * Two entry points produce a request:
 *
 * - Notification deep-link routing (`source: "notification"`) — wired
 *   by the parent's effect-based router; this hook just exposes the
 *   setter.
 * - Pinned-panel "Scroll to message" + "Scroll to parent" buttons
 *   (`source: "in-app-jump"`) — `handleScrollToMessage`.
 *
 * Each request is keyed by a monotonically-increasing `requestId` so
 * the list view can ack the exact request it consumed, even if the
 * parent re-mints another for the same message in the meantime.
 *
 * `handleScrollRequestHandled(requestId, source)` ignores stale acks
 * and forwards the notification ack to `onTargetMessageHandled` so the
 * parent can clear `targetMessageId`.
 *
 * `setScrollRequestForNotification(messageId)` is the de-duped variant
 * used by the notification-routing effect: it returns `prev` if an
 * active request already exists for the same message, preventing the
 * re-entry loop where the effect re-fires its own setter.
 */
export function useScrollRequest({
  onTargetMessageHandled,
}: UseScrollRequestArgs) {
  const scrollRequestIdRef = useRef(0);
  const [scrollRequest, setScrollRequest] = useState<ScrollRequest | null>(
    null,
  );

  const setScrollRequestForNotification = useCallback((messageId: string) => {
    const requestId = ++scrollRequestIdRef.current;
    setScrollRequest((prev) => {
      // Don't re-enter if we already have an active request for this message
      if (prev && prev.messageId === messageId) return prev;
      return {
        messageId,
        source: "notification",
        requestId,
      };
    });
  }, []);

  const handleScrollToMessage = useCallback((messageId: string) => {
    const requestId = ++scrollRequestIdRef.current;
    setScrollRequest({
      messageId,
      source: "in-app-jump",
      requestId,
    });
  }, []);

  const handleScrollRequestHandled = useCallback(
    (requestId: number, source: "notification" | "in-app-jump") => {
      setScrollRequest((prev) => {
        if (!prev || prev.requestId !== requestId) return prev;
        return null;
      });
      if (source === "notification") {
        onTargetMessageHandled?.();
      }
    },
    [onTargetMessageHandled],
  );

  return {
    scrollRequest,
    setScrollRequestForNotification,
    handleScrollToMessage,
    handleScrollRequestHandled,
  };
}
